import { useEffect, useSyncExternalStore } from "react";
import { fetchJoinRequestsWaitingForMe, type EquipoConSolicitudes } from "../../api/player";

/**
 * "Solicitudes de ingreso que me esperan a mí" para el flujo Jugador de la PWA.
 *
 * POR QUÉ EXISTE (03-11-2026). La solicitud le llegaba al capitán por una push
 * y un mail, y después vivía SÓLO adentro de la pestaña Equipo: había que
 * entrar para saber si había que entrar. La push es de un solo intento y la
 * fila que el server escribe en `notifications` para él no la lee nadie —
 * `/me/notifications` lo consume únicamente el organizador. Ver
 * `docs/PROPUESTA_PENDIENTES_DEL_JUGADOR.md`.
 *
 * Gemelo de `features/jugador/SolicitudesStore.kt` en Android, con el mismo
 * reparto: el Inicio dibuja una tarjeta por equipo y el cajón pone el globito
 * en "Mi equipo", y los dos leen de acá. Dos contadores sueltos para lo mismo
 * terminan diciendo cosas distintas.
 */

let equipos: EquipoConSolicitudes[] = [];
let ultimoMs = 0;
let pidiendo = false;
const oyentes = new Set<() => void>();

const TTL_MS = 5 * 60_000;
const VACIO: EquipoConSolicitudes[] = [];

function avisar() {
  for (const oyente of oyentes) oyente();
}

/** Logout: que la próxima cuenta no herede las solicitudes de la anterior. */
export function resetSolicitudesQueEsperan() {
  if (equipos.length === 0 && ultimoMs === 0) return;
  equipos = VACIO;
  ultimoMs = 0;
  avisar();
}

/**
 * Pregunta al server. Con TTL porque lo llama cada pantalla que muestra el
 * número; se traga el error a propósito — una tarjeta que no se pudo
 * actualizar no es motivo para romper el Inicio.
 */
export async function refrescarSolicitudesQueEsperan(forzar = false) {
  const ahora = Date.now();
  if (!forzar && (pidiendo || ahora - ultimoMs < TTL_MS)) return;
  pidiendo = true;
  try {
    const respuesta = await fetchJoinRequestsWaitingForMe();
    // Un equipo en cero no tiene nada que mostrar.
    const nuevos = (respuesta.equipos ?? []).filter((e) => (e.pendientes ?? 0) > 0);
    // Misma identidad si no cambió nada: useSyncExternalStore compara por
    // referencia y un array nuevo en cada refresco re-renderiza el Inicio
    // entero cada cinco minutos sin que haya pasado nada.
    const igual =
      nuevos.length === equipos.length &&
      nuevos.every((e, i) => e.teamId === equipos[i].teamId && e.pendientes === equipos[i].pendientes);
    if (!igual) {
      equipos = nuevos.length === 0 ? VACIO : nuevos;
      avisar();
    }
    ultimoMs = Date.now();
  } catch {
    // Sin red, sin sesión, lo que sea: queda lo último conocido.
  } finally {
    pidiendo = false;
  }
}

/**
 * Los equipos con solicitudes esperando. Pide al montar y al volver a la
 * pestaña (en la PWA instalada, el equivalente de "volver a la app").
 */
export function useSolicitudesQueEsperan(): EquipoConSolicitudes[] {
  const valor = useSyncExternalStore(
    (alCambiar) => {
      oyentes.add(alCambiar);
      return () => {
        oyentes.delete(alCambiar);
      };
    },
    () => equipos,
    () => VACIO,
  );

  useEffect(() => {
    void refrescarSolicitudesQueEsperan();
    const alVolver = () => {
      if (document.visibilityState === "visible") void refrescarSolicitudesQueEsperan(true);
    };
    document.addEventListener("visibilitychange", alVolver);
    return () => document.removeEventListener("visibilitychange", alVolver);
  }, []);

  return valor;
}
